import { BASE_URL } from "../constants";
import { getAuthHeader } from "../utils";

/**
 * @typedef Insurance
 * @property {number} id
 * @property {string} logo
 * @property {string} name
 */


export async function getInsurance() {
  try {
    const response = await fetch(`${BASE_URL}insurance/get-insurance/`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error(JSON.stringify(response));
    }
    /**@type {Insurance} */
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching medical center", error);
    return null;
  }
}

/**
 * Devuelve todas las aseguradoras (requiere token de admin).
 * @returns {Promise<Insurance[]|null>}
 */
export async function getAllInsurances() {
  try {
    const response = await fetch(`${BASE_URL}insurance/insurances/`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      let bodyText = null;
      try {
        // try to parse json or text for better error info
        bodyText = await response.text();
      } catch (readErr) {
        bodyText = `<unable to read body: ${readErr.message}>`;
      }
      console.error(`getAllInsurances: non-OK response ${response.status} ${response.statusText}`, bodyText);
      // keep returning null to preserve existing behavior, but log details
      return null;
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error fetching insurances", error?.message || error);
    return null;
  }
}

/**
 * Devuelve los usuarios asociados a una aseguradora (paginado).
 * @param {number|string} insuranceId
 * @param {{search?: string, page?: number, page_size?: number}} params
 * @returns {Promise<Object|null>} objeto paginado {count,next,previous,results}
 */
export async function getInsuranceUsers(insuranceId, params = {}) {
  try {
    const qs = new URLSearchParams();
    if (params.search) qs.set("search", params.search);
    if (params.page) qs.set("page", String(params.page));
    if (params.page_size) qs.set("page_size", String(params.page_size));

    const url = `${BASE_URL}insurance/insurances/${insuranceId}/users/${qs.toString() ? `?${qs.toString()}` : ""}`;
    const response = await fetch(url, {
      headers: {
        ...getAuthHeader(),
      },
    });

    if (!response.ok) {
      let bodyText = null;
      try {
        bodyText = await response.text();
      } catch (readErr) {
        bodyText = `<unable to read body: ${readErr.message}>`;
      }
      console.error(`getInsuranceUsers: non-OK response ${response.status} ${response.statusText}`, bodyText);
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching insurance users", error?.message || error);
    return null;
  }
}

/**
 * Update an insurance (name, logo file, associated users).
 * Sends multipart/form-data and returns the parsed JSON response or null on error.
 * @param {number|string} insuranceId
 * @param {{name?: string, logoFile?: File, associatedUsers?: Array<number>}} payload
 */
export async function updateInsurance(insuranceId, payload = {}) {
  try {
    const form = new FormData();
    if (payload.name !== undefined) form.append("name", String(payload.name));
    if (payload.logoFile) form.append("logo", payload.logoFile);
    if (payload.associatedUsers !== undefined) {
      // send as JSON string; backend will parse this field
      form.append("associated_users", JSON.stringify(payload.associatedUsers));
    }

    // Ensure we don't set Content-Type so the browser can add the multipart boundary
    const headers = { ...(getAuthHeader() || {}) };

    const response = await fetch(`${BASE_URL}insurance/insurances/${insuranceId}/`, {
      method: "PATCH",
      headers,
      body: form,
    });

    if (!response.ok) {
      let bodyText = null;
      try {
        bodyText = await response.text();
      } catch (readErr) {
        bodyText = `<unable to read body: ${readErr.message}>`;
      }
      console.error(`updateInsurance: non-OK response ${response.status} ${response.statusText}`, bodyText);
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating insurance", error?.message || error);
    return null;
  }
}

/**
 * Create a new insurance.
 * Expects multipart/form-data fields: name, logo (file), associated_users (JSON array string)
 * @param {{name?: string, logoFile?: File, associatedUsers?: Array<number|string>}} payload
 */
export async function createInsurance(payload = {}) {
  try {
    const form = new FormData();
    if (payload.name !== undefined) form.append("name", String(payload.name));
    if (payload.logoFile) form.append("logo", payload.logoFile);
    if (payload.associatedUsers !== undefined) {
      form.append("associated_users", JSON.stringify(payload.associatedUsers));
    }

    const headers = { ...(getAuthHeader() || {}) };

    const response = await fetch(`${BASE_URL}insurance/insurances/`, {
      method: "POST",
      headers,
      body: form,
    });

    if (!response.ok) {
      let bodyText = null;
      try {
        bodyText = await response.text();
      } catch (readErr) {
        bodyText = `<unable to read body: ${readErr.message}>`;
      }
      console.error(`createInsurance: non-OK response ${response.status} ${response.statusText}`, bodyText);
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating insurance", error?.message || error);
    return null;
  }
}

/**
 * Delete an insurance by id.
 * Returns true on success, null on error.
 * @param {number|string} insuranceId
 */
export async function deleteInsurance(insuranceId) {
  try {
    const headers = { ...(getAuthHeader() || {}) };
    const response = await fetch(`${BASE_URL}insurance/insurances/${insuranceId}/`, {
      method: "DELETE",
      headers,
    });
    if (!response.ok) {
      let bodyText = null;
      try {
        bodyText = await response.text();
      } catch (readErr) {
        bodyText = `<unable to read body: ${readErr.message}>`;
      }
      console.error(`deleteInsurance: non-OK response ${response.status} ${response.statusText}`, bodyText);
      return null;
    }
    return true;
  } catch (error) {
    console.error("Error deleting insurance", error?.message || error);
    return null;
  }
}